import {
	Flex,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	useDisclosure,
	Text,
	Button,
	Badge,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import PrimaryButton from "../components/PrimaryButton";
import { AddIcon } from "@chakra-ui/icons";
import PersistingTabIndex, {
	PersistingTabIndexHandler,
} from "../components/PersistingTabIndex";
import DataTable, { Cell } from "../components/table/ServerSideTable";
import { useSupplies } from "../hooks/supplies.hooks";
import { useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Supply } from "../types/supplies.types";
import SimpleModal from "../components/modals/SimpleModal";
import PlusIcon from "../components/icons/PlusIcon";
import SupplyForm, { SupplyFormHandler } from "../components/forms/SupplyForm";
import { useMutation, useQueryClient } from "react-query";
import {
	createSupply,
	deleteSupply,
	updateSupply,
} from "../mutations/supply.mutations";
import TrashIcon from "../components/icons/TrashIcon";
import EarmoldTab from "./EarmoldTab";
import { createEarmold } from "../mutations/earmold.mutations";
import EarmoldForm, {
	EarmoldFormHandler,
} from "../components/forms/EarmoldForm";
import ProductBadge from "../components/ProductBadge";
import MaterialTab from "./MaterialTab";
import MaterialForm, {
	MaterialFormHandler,
} from "../components/forms/MaterialForm";
import {
	createMaterial,
	updateMaterial,
} from "../mutations/material.mutations";
import { Material, MaterialStatus } from "../types/material.types";
import FilePreview from "../components/FilePreview";
import { useSortable } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { useChangeSupplyOrder } from "../mutations/supplies.mutations";

export function SortableItem({ id }: { id: string }) {
	const { attributes, listeners, transition, transform, setNodeRef } =
		useSortable({ id });

	const style = {
		padding: "0rem 0.6rem 0rem 0rem",
		transform: transform
			? `translate3d(${transform.x}px, ${transform.y}px, 0)`
			: undefined,
		transition,
	};
	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			⣿
		</div>
	);
}

const SupplyTable = ({
	handleCellClick,
}: {
	handleCellClick?: (cell: Cell) => void;
}) => {
	const columns = useMemo<ColumnDef<Supply>[]>(
		() => [
			{
				accessorKey: "index",
				header: "",
				cell: (info) => <SortableItem id={info.getValue() as string} />,
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Flex h="100%" alignItems={"center"}>
						{row.fileId && <FilePreview id={row.fileId} mr={4}></FilePreview>}
						<Text>{row.label}</Text>
					</Flex>
				),
				id: "label",
				header: "Fourniture",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => `${Math.round(row.price) / 100} €`,
				id: "price",
				header: "Prix à l'unité",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <ProductBadge status={row.status}></ProductBadge>,
				id: "status",
				header: "Statut",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => {
					const stock = Number.parseInt(row.stock.toString());
					if (stock === 0) {
						return (
							<Badge
								h="1.5rem"
								lineHeight="1.5rem"
								px="1rem"
								rounded="xl"
								backgroundColor={"white"}
								color={"#B91C1C"}
								border={"solid 1px"}
								borderColor={"#FCA5A5"}
								textTransform={"capitalize"}
								w="fit-content"
							>
								0 en stock
							</Badge>
						);
					}
					return (
						<Badge
							h="1.5rem"
							lineHeight="1.5rem"
							px="1rem"
							rounded="xl"
							backgroundColor={"white"}
							color={"gray.700"}
							border={"solid 1px"}
							borderColor={"gray.300"}
							textTransform={"capitalize"}
							w="fit-content"
						>
							{stock}
						</Badge>
					);
				},
				id: "stock",
				header: "Inventaire",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	const queryClient = useQueryClient();
	const { mutate: changeSupplyOrder } = useChangeSupplyOrder({
		onSuccess: () => {
			queryClient.invalidateQueries("supplies");
		},
	});

	return (
		<DataTable
			columns={columns}
			paginatedQuery={useSupplies}
			w={["auto", "auto", "10rem", "10rem", "10rem"]}
			flexGrow={[0, 1, 0, 0, 0]}
			handleCellClick={handleCellClick}
			defaultSortBy={[{ id: "index", desc: false }]}
			onDragEnd={(event: DragEndEvent) => {
				const { active, over } = event;
				if (!over) return;

				const prev = Number.parseInt(active.id as string);
				const next = Number.parseInt(over.id as string);
				changeSupplyOrder({ prev, next });
			}}
		></DataTable>
	);
};

function ProductPage() {
	const supplyModal = useDisclosure();
	const earmoldModal = useDisclosure();
	const materialModal = useDisclosure();
	const tabRef = useRef<PersistingTabIndexHandler>(null);
	const [selectedSupply, setSelectedSupply] = useState<Supply | undefined>(
		undefined,
	);
	const [selectedMaterial, setSelectedMaterial] = useState<
		Material | undefined
	>(undefined);
	const supplyModalRef = useRef<SupplyFormHandler>(null);
	const materialModalRef = useRef<MaterialFormHandler>(null);
	const earmoldModalRef = useRef<EarmoldFormHandler>(null);
	const queryClient = useQueryClient();
	const { mutate: mutateCreateSupply } = useMutation(createSupply, {
		onSuccess: () => {
			queryClient.invalidateQueries("supplies");
			supplyModal.onClose();
		},
	});
	const { mutate: mutateUpdateSupply } = useMutation(updateSupply, {
		onSuccess: () => {
			queryClient.invalidateQueries("supplies");
			supplyModal.onClose();
		},
	});
	const { mutate: mutateDeleteSupply } = useMutation(deleteSupply, {
		onSuccess: () => {
			queryClient.invalidateQueries("supplies");
			supplyModal.onClose();
		},
	});
	const { mutate: mutateCreateEarmold } = useMutation(createEarmold, {
		onSuccess: () => {
			queryClient.invalidateQueries("earmolds");
			earmoldModal.onClose();
		},
	});
	const { mutate: mutateCreateMaterial } = useMutation(createMaterial, {
		onSuccess: () => {
			queryClient.invalidateQueries("materials-paginated");
			materialModal.onClose();
		},
	});
	const { mutate: mutateUpdateMaterial } = useMutation(updateMaterial, {
		onSuccess: () => {
			queryClient.invalidateQueries("materials-paginated");
			materialModal.onClose();
		},
	});
	const handleSupplyOnSave = async () => {
		if (supplyModalRef.current) {
			const valid = await supplyModalRef.current.valid();
			if (valid) {
				if (selectedSupply === undefined) {
					mutateCreateSupply(supplyModalRef.current.values());
				} else {
					mutateUpdateSupply({
						id: selectedSupply.id,
						...supplyModalRef.current.values(),
					});
				}
			}
		}
	};
	const handleMaterialOnSave = async () => {
		if (materialModalRef.current) {
			const valid = await materialModalRef.current.valid();
			if (valid) {
				if (selectedMaterial === undefined) {
					mutateCreateMaterial(materialModalRef.current.values());
				} else {
					mutateUpdateMaterial({
						id: selectedMaterial.id,
						...materialModalRef.current.values(),
					});
				}
			}
		}
	};
	const handleEarmoldOnSave = async () => {
		if (earmoldModalRef.current) {
			const valid = await earmoldModalRef.current.valid();
			if (valid) {
				if (selectedSupply === undefined) {
					mutateCreateEarmold(earmoldModalRef.current.values());
				}
			}
		}
	};
	return (
		<>
			<Flex justifyContent={"space-between"}>
				<PageTitle>Produits</PageTitle>
				<PrimaryButton
					w={"fit-content"}
					onClick={() => {
						if (tabRef.current) {
							const tab = tabRef.current.getTab();
							switch (tab) {
								case 0:
									earmoldModal.onOpen();
									break;
								case 1:
									setSelectedMaterial(undefined);
									materialModal.onOpen();
									break;
								case 2:
									setSelectedSupply(undefined);
									supplyModal.onOpen();
									break;
								default:
									break;
							}
						}
					}}
				>
					<AddIcon mr={4}></AddIcon>Ajouter
				</PrimaryButton>
			</Flex>

			<PersistingTabIndex id="products-tab" mt={6} ref={tabRef}>
				<TabList>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Embouts
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Matériaux
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Fournitures
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<EarmoldTab></EarmoldTab>
					</TabPanel>
					<TabPanel px={0}>
						<MaterialTab
							onUpdate={(material: Material) => {
								setSelectedMaterial(material);
								materialModal.onOpen();
							}}
							onUpdateStatus={(material: Material, status: MaterialStatus) => {
								mutateUpdateMaterial({
									id: material.id,
									status,
								});
							}}
						></MaterialTab>
					</TabPanel>
					<TabPanel px={0}>
						<SupplyTable
							handleCellClick={(cell: Cell) => {
								setSelectedSupply(cell.row);
								supplyModal.onOpen();
							}}
						></SupplyTable>

						<SimpleModal
							icon={<PlusIcon color="brand.700"></PlusIcon>}
							title="Ajouter un embouts"
							isOpen={earmoldModal.isOpen}
							onClose={earmoldModal.onClose}
							onClick={handleEarmoldOnSave}
							w={"40%"}
						>
							<EarmoldForm ref={earmoldModalRef}></EarmoldForm>
						</SimpleModal>

						<SimpleModal
							icon={<PlusIcon color="brand.700"></PlusIcon>}
							title={
								selectedMaterial
									? "Modifier un matériau"
									: "Ajouter un matériau"
							}
							isOpen={materialModal.isOpen}
							onClose={materialModal.onClose}
							onClick={handleMaterialOnSave}
							isUpdate={selectedMaterial != undefined}
						>
							<MaterialForm ref={materialModalRef} value={selectedMaterial} />
						</SimpleModal>

						<SimpleModal
							icon={<PlusIcon color="brand.700"></PlusIcon>}
							title={
								selectedSupply === undefined
									? "Ajouter un produit"
									: "Modifier un produit"
							}
							isOpen={supplyModal.isOpen}
							onClose={supplyModal.onClose}
							onClick={handleSupplyOnSave}
							isUpdate={selectedSupply !== undefined}
							w={"40%"}
						>
							<SupplyForm
								ref={supplyModalRef}
								value={selectedSupply}
							></SupplyForm>

							{selectedSupply && (
								<Flex w="100%" justifyContent="center" my={6}>
									<Button
										color="red.700"
										variant="link"
										onClick={() => {
											if (selectedSupply) {
												mutateDeleteSupply({
													id: selectedSupply.id,
												});
											}
										}}
									>
										<TrashIcon mr={4}></TrashIcon>Supprimer le produit
									</Button>
								</Flex>
							)}
						</SimpleModal>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default ProductPage;
